import { useJobillaOrgId } from '@/hooks'
import { apiErrorMessage } from '@/lib'
import {
	mediaImageIndexQueryKey,
	MediaImageUploadMutationResponse,
	useMediaImageIndex,
	useMediaImageUpload,
} from '@jobilla/nexus-sdk'
import { useCustomFontIndex } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { useSetAtom } from 'jotai'
import { useEffect } from 'react'
import {
	emitUnhandledError,
	formatFileValidationError,
	logger,
	MediaAsset,
	OrgAssets,
	useAlert,
	useEditorOrgAssets,
	validateImageFile,
} from '../../ui'
import { customFontsAtom, customFontsLoadingAtom } from '../../ui'

export type UseNewEditorOrgAssetsReturns = {
	onUpload: (files: FileList, collection: MediaAsset['collection']) => void
	onUploadAsync: (file: File, collection: MediaAsset['collection']) => Promise<MediaImageUploadMutationResponse>
}

export function useNewEditorOrgAssets(): UseNewEditorOrgAssetsReturns {
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const alert = useAlert()

	const assets = useMediaImageIndex()

	// Transform HTTP data to atom shape
	const orgAssets: OrgAssets | undefined = assets.data
		? {
			media: assets.data.map((asset) => ({
				id: asset.id,
				url: asset.url,
				category: 'image' as const,
				name: asset.fileName,
				width: asset.width,
				height: asset.height,
				fileName: asset.fileName,
				humanReadableSize: asset.humanReadableSize,
				createdAt: new Date(asset.createdAt),
				collection: asset.collectionName,
				isReady: true,
			})),
		}
		: undefined

	const organisationId = jobillaOrgId

	// Fetch custom fonts and sync to editor state
	const { data: customFontsData, isLoading: customFontsLoading } = useCustomFontIndex(organisationId)
	const setCustomFonts = useSetAtom(customFontsAtom)
	const setCustomFontsLoading = useSetAtom(customFontsLoadingAtom)
	useEffect(() => {
		setCustomFonts(customFontsData ?? [])
	}, [customFontsData, setCustomFonts])
	useEffect(() => {
		setCustomFontsLoading(customFontsLoading)
	}, [customFontsLoading, setCustomFontsLoading])

	const imageStoreMutation = useMediaImageUpload({
		mutationKey: ['media-image-upload'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
		},
		onError: (error) => {
			logger.error(error, {
				tags: { component: 'EditorOrgAssets', action: 'upload' },
			})
			emitUnhandledError({
				title: i18next.t('editor:errors.uploadFailed'),
				description: apiErrorMessage(error) ?? i18next.t('editor:errors.unexpectedError'),
				cause: error,
			})
		},
	})

	const handleUpload = (files: FileList, collection: MediaAsset['collection']) => {
		const file = files[0]
		if (!file) { return }

		// Validate file before upload
		const validationError = validateImageFile(file)
		if (validationError) {
			alert.open(
				validationError.type === 'file-too-large'
					? i18next.t('editor:imageValidation.imageTooLargeTitle')
					: i18next.t('editor:imageValidation.invalidFileTypeTitle'),
				formatFileValidationError(validationError),
				'OK',
				() => {},
			)
			return
		}

		if (!organisationId) { return }

		imageStoreMutation.mutate({ organisation: organisationId, data: { media: file, collection } })
	}

	const handleUploadAsync = async (file: File, collection: MediaAsset['collection']) => {
		const validationError = validateImageFile(file)
		if (validationError) {
			alert.open(
				validationError.type === 'file-too-large'
					? i18next.t('editor:imageValidation.imageTooLargeTitle')
					: i18next.t('editor:imageValidation.invalidFileTypeTitle'),
				formatFileValidationError(validationError),
				'OK',
				() => {},
			)
			return Promise.reject(new Error(formatFileValidationError(validationError)))
		}

		if (!organisationId) {
			return Promise.reject(new Error('Organisation ID not available'))
		}

		return imageStoreMutation.mutateAsync({ organisation: organisationId, data: { media: file, collection } })
			.catch((error) => {
				logger.error(error, {
					tags: { component: 'EditorOrgAssets', action: 'upload-async' },
				})
				emitUnhandledError({
					title: i18next.t('editor:errors.uploadFailed'),
					description: error?.message ?? String(error),
					cause: error,
				})
				throw error
			})
	}

	const editorResult = useEditorOrgAssets({
		orgAssets,
		onUpload: handleUpload,
	})

	return {
		onUpload: editorResult.onUpload,
		onUploadAsync: handleUploadAsync,
	}
}
